import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Collapse,
  makeStyles,
  CircularProgress,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import axios from 'axios';
import { appUrl } from '../../../../../utils/config';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
  },
  titleText: {
    fontWeight: 600,
    fontSize: '1.5rem',
    color: '#000',
  },
  cancelButton: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: '#000',
    fontSize: '1rem',
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  dialogContent: {
    padding: theme.spacing(2, 3),
  },
  fieldLabel: {
    fontSize: '0.875rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: '#000',
    display: 'block',
  },
  requiredAsterisk: {
    color: '#FF0000',
    marginLeft: theme.spacing(0.25),
    marginRight: theme.spacing(0.5),
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      height: 40,
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 0px',
    },
    '& .MuiSelect-select': {
      paddingLeft: 5,
    },
    '& .MuiAutocomplete-input': {
      padding: '8px 0px !important',
    },
    '& .MuiAutocomplete-inputRoot': {
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  expandableSection: {
    marginTop: theme.spacing(2),
  },
  expandableHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
    cursor: 'pointer',
  },
  expandableTitle: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: '#000',
  },
  tableContainer: {
    boxShadow: 'none',
    border: '1px solid #E0E0E0',
  },
  table: {
    '& .MuiTableCell-root': {
      padding: theme.spacing(1.5, 2),
    },
    '& .MuiTableRow-root': {
      '&:not(:first-child)': {
        borderTop: '1px solid #E0E0E0',
      },
    },
  },
  tableHeader: {
    fontWeight: 600,
    color: '#000',
    backgroundColor: '#F5F5F5',
  },
  continueButton: {
    backgroundColor: '#e36e39',
    color: '#FFFFFF',
    borderRadius: '20px',
    padding: '8px 32px',
    textTransform: 'none',
    fontWeight: 500,
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: '#c44d15',
      color: 'white',
      boxShadow: '0 4px 12px rgba(227, 110, 57, 0.4)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 2px 6px rgba(227, 110, 57, 0.3)',
    },
    '&:disabled': {
      backgroundColor: '#CCCCCC',
      color: '#666666',
      cursor: 'not-allowed',
    },
  },
  dialogActions: {
    padding: theme.spacing(3, 3, 3, 3),
    justifyContent: 'center',
    paddingBottom: theme.spacing(4),
  },
}));

const ResetBatchDialogInactive = ({ open, onClose, onContinue, isLoading = false }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchesList, setBatchesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reasonForReset, setReasonForReset] = useState('');

  const batchInfo = {
    poNumber: selectedBatch?.scheduleAgreementNumber || '',
    cmo: selectedBatch?.vendorName || '',
    batchStatus: selectedBatch?.batchStatusText || '',
  };

  // Only one reason option for inactive PO dashboard (per TDD F2)
  const allResetReasons = [
    'Document Resubmission without digitalization of the document',
  ];

  const allowedStatuses = [
    {
      reason: 'Document Resubmission without digitalization of the document',
      allowedStatus: 'All',
    },
  ];

  useEffect(() => {
    if (open) {
      fetchBatches();
      setSelectedBatch(null);
      setReasonForReset('');
      setExpanded(false);
    }
  }, [open]);

  const fetchBatches = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchAllFGBatches/false`
      );
      if (response.data?.status && response.data?.batchesList) {
        setBatchesList(response.data.batchesList);
      } else {
        setBatchesList([]);
      }
    } catch (error) {
      setBatchesList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBatchChange = (_, value) => {
    setSelectedBatch(value);
  };

  const handleContinue = async () => {
    try {
      let documents = [];

      try {
        // Static data response (simulating API response until backend is ready)
        documents = [
          { documentTypeName: 'Packing Slip' },
          { documentTypeName: 'Bill of Lading' },
          { documentTypeName: 'Certificate of Analysis' },
          { documentTypeName: 'Invoice' },
          { documentTypeName: 'Other' },
        ];
      } catch (apiError) {
        console.error('Error fetching reset documents:', apiError);
        documents = [
          { documentTypeName: 'Packing Slip' },
          { documentTypeName: 'Bill of Lading' },
          { documentTypeName: 'Certificate of Analysis' },
          { documentTypeName: 'Invoice' },
          { documentTypeName: 'Other' },
        ];
      }

      onContinue({
        poNumber: batchInfo.poNumber,
        batchNumber: selectedBatch?.vendorBatchNumber || '',
        cmo: batchInfo.cmo,
        batchStatus: batchInfo.batchStatus,
        reasonForReset: reasonForReset,
        schrockenDeliveryScheduleBatchID: selectedBatch?.schrockenDeliveryScheduleBatchID || '',
        vendorMosymphonyID: selectedBatch?.vendorMosymphonyID || '',
        batchStatusID: selectedBatch?.SASLBatchStatusID || '',
        brandOwnerMosymphonyID: selectedBatch?.brandOwnerMosymphonyID || '',
        documents: documents,
      });
    } catch (error) {
      console.error('Error in handleContinue:', error);
      onContinue({
        poNumber: batchInfo.poNumber,
        batchNumber: selectedBatch?.vendorBatchNumber || '',
        cmo: batchInfo.cmo,
        batchStatus: batchInfo.batchStatus,
        reasonForReset: reasonForReset,
        schrockenDeliveryScheduleBatchID: selectedBatch?.schrockenDeliveryScheduleBatchID || '',
        vendorMosymphonyID: selectedBatch?.vendorMosymphonyID || '',
        batchStatusID: selectedBatch?.SASLBatchStatusID || '',
        brandOwnerMosymphonyID: selectedBatch?.brandOwnerMosymphonyID || '',
        documents: [
          { documentTypeName: 'Packing Slip' },
          { documentTypeName: 'Bill of Lading' },
          { documentTypeName: 'Certificate of Analysis' },
          { documentTypeName: 'Invoice' },
          { documentTypeName: 'Other' },
        ],
      });
    }
  };

  const isFormValid = selectedBatch !== null && reasonForReset !== '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '8px',
          maxWidth: '900px',
        },
      }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
              <Typography className={classes.cancelButton} onClick={onClose}>
                Cancel <CloseIcon style={{ fontSize: '24px', marginLeft: '4px', color: '#FF0000' }} />
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Typography className={classes.titleText}>Reset Batch</Typography>
            </div>
          </div>
        </div>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Batch Information Section */}
        <Grid container spacing={3} style={{ marginTop: '8px' }}>
          <Grid item xs={3} sm={2.5}>
            <Typography className={classes.fieldLabel} style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, textAlign: 'left' }}>
              Batch Number <span className={classes.requiredAsterisk}>*</span>
            </Typography>
            <Autocomplete
              options={batchesList}
              loading={loading}
              getOptionLabel={(option) => option?.vendorBatchNumber || ''}
              value={selectedBatch}
              onChange={handleBatchChange}
              selectOnFocus={false}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={loading ? 'Loading...' : 'Select option'}
                  className={classes.textField}
                />
              )}
            />
          </Grid>
          <Grid item xs={3} sm={3.17} style={{ paddingLeft: '16px' }}>
            <Typography className={classes.fieldLabel}>PO Number</Typography>
            <div style={{ minHeight: '40px', display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1" style={{ color: '#000', fontSize: '0.875rem' }}>
                {batchInfo.poNumber}
              </Typography>
            </div>
          </Grid>
          <Grid item xs={3} sm={3.17}>
            <Typography className={classes.fieldLabel}>CMO</Typography>
            <div style={{ minHeight: '40px', display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1" style={{ color: '#000', fontSize: '0.875rem' }}>
                {batchInfo.cmo}
              </Typography>
            </div>
          </Grid>
          <Grid item xs={3} sm={3.16}>
            <Typography className={classes.fieldLabel}>Batch Status</Typography>
            <div style={{ minHeight: '40px', display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1" style={{ color: '#000', fontSize: '0.875rem' }}>
                {batchInfo.batchStatus}
              </Typography>
            </div>
          </Grid>
        </Grid>

        {/* Reason for Reset Section */}
        <Grid container spacing={2} style={{ marginTop: '20px' }}>
          <Grid item xs={12}>
            <Typography className={classes.fieldLabel}>
              Reason for Reset <span className={classes.requiredAsterisk}>*</span>
            </Typography>
            <FormControl fullWidth variant="outlined">
              <Select
                value={reasonForReset}
                onChange={(e) => setReasonForReset(e.target.value)}
                displayEmpty
                className={classes.textField}
                disabled={!selectedBatch}
              >
                <MenuItem value="" disabled>
                  {selectedBatch ? 'Select from the dropdown' : 'Please select a batch first'}
                </MenuItem>
                {allResetReasons.map((reason, index) => (
                  <MenuItem key={index} value={reason}>
                    {reason}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Expandable Allowed Batch Statuses Section */}
        <div className={classes.expandableSection} style={{ marginTop: '16px' }}>
          <div className={classes.expandableHeader} onClick={() => setExpanded(!expanded)}>
            {!expanded ? (
              <ChevronRightIcon style={{ color: '#666', marginRight: '8px' }} />
            ) : (
              <ExpandMoreIcon style={{ color: '#666', marginRight: '8px' }} />
            )}
            <Typography className={classes.expandableTitle}>
              Allowed Batch Statuses for each Reason for Reset
            </Typography>
          </div>
          <Collapse in={expanded}>
            <TableContainer component={Paper} className={classes.tableContainer}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeader} style={{ width: '60%' }}>
                      Reason for Reset
                    </TableCell>
                    <TableCell className={classes.tableHeader} style={{ width: '40%' }}>
                      Allowed Batch Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allowedStatuses.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell style={{ whiteSpace: 'pre-line', width: '60%' }}>
                        {item.reason}
                      </TableCell>
                      <TableCell style={{ width: '40%' }}>{item.allowedStatus}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </div>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleContinue}
          disabled={!isFormValid || isLoading}
          className={classes.continueButton}
        >
          {isLoading ? (
            <>
              <CircularProgress size={16} style={{ marginRight: '8px', color: '#FFFFFF' }} />
              Processing...
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResetBatchDialogInactive;
